import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, AlertTriangle, Wallet, FileUp, Hash, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function WalletRecargar() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [cfg, setCfg] = useState(null);
    const [tab, setTab] = useState("bre_b");
    const [submitting, setSubmitting] = useState(false);
    const [last, setLast] = useState(null);
    const [history, setHistory] = useState([]);

    // Bre-B form
    const prefill = Number(params.get("monto") || 0);
    const [copAmount, setCopAmount] = useState(prefill > 0 ? prefill : 50000);
    const [holderName, setHolderName] = useState("");
    const [proofFile, setProofFile] = useState(null);
    const [proofDataUrl, setProofDataUrl] = useState("");

    // Crypto form
    const [usdtAmount, setUsdtAmount] = useState(20);
    const [txHash, setTxHash] = useState("");

    const intent = params.get("intent");

    useEffect(() => {
        api.get("/wallet/config/public").then((r) => setCfg(r.data));
        if (user && user !== false) {
            api.get("/deposits/me").then((r) => setHistory(r.data || []));
        }
    }, [user]);

    if (user === false) return <Navigate to="/login" replace />;
    if (!user) return null;

    const copy = (text, label) => {
        navigator.clipboard.writeText(text).then(() => toast.success(`${label} copiado`));
    };

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) {
            toast.error("Archivo demasiado grande (máx 5MB)");
            return;
        }
        setProofFile(f);
        const r = new FileReader();
        r.onload = (ev) => setProofDataUrl(ev.target.result);
        r.readAsDataURL(f);
    };

    const submitBreB = async () => {
        if (!copAmount || copAmount < 5000) { toast.error("Monto mínimo COP 5.000"); return; }
        if (!holderName || holderName.length < 3) { toast.error("Indica el nombre del titular"); return; }
        if (!proofDataUrl) { toast.error("Sube el comprobante de pago"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/deposits", {
                method: "bre_b",
                amount: copAmount,
                currency: "COP",
                holder_name: holderName,
                proof_url: proofDataUrl,
            });
            setLast(data);
            toast.success(`Solicitud enviada. Ref ${data.reference_code}`);
            await refresh();
            const h = await api.get("/deposits/me");
            setHistory(h.data || []);
            if (intent) {
                toast.message("Cuando aprueben tu recarga, vuelve al mercado para confirmar tu apuesta.");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    const submitCrypto = async () => {
        if (!usdtAmount || usdtAmount < 5) { toast.error("Monto mínimo 5 USDT"); return; }
        if (!txHash || txHash.length < 10) { toast.error("Pega el TX hash de la transferencia"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/deposits", {
                method: "crypto",
                amount: Math.round(usdtAmount),
                currency: "USDT",
                tx_hash: txHash.trim(),
            });
            setLast(data);
            toast.success(`Solicitud enviada. Ref ${data.reference_code}`);
            await refresh();
            const h = await api.get("/deposits/me");
            setHistory(h.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    if (!cfg) return <div className="max-w-3xl mx-auto px-6 py-16"><div className="h-96 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" /></div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="wallet-recargar-page">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-p2w-gray hover:text-white transition mb-5">
                <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-p2w-green font-sora font-bold">
                <Wallet className="w-3.5 h-3.5" /> Recargar saldo
            </div>
            <h1 className="font-sora font-extrabold text-4xl sm:text-5xl tracking-tighter mt-2">Recargar saldo</h1>
            <p className="mt-2 text-p2w-gray">
                Elige el método de pago. Tu saldo se acredita tras la verificación.
                {prefill > 0 && (
                    <span className="ml-1 text-p2w-amber">Te faltan {prefill.toLocaleString("es-CO")} para tu apuesta.</span>
                )}
            </p>

            <Tabs value={tab} onValueChange={setTab} className="mt-8">
                <TabsList className="bg-p2w-elevated border border-p2w-line">
                    <TabsTrigger value="bre_b" data-testid="tab-bre-b" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg gap-2">
                        Transferencia · Bre-B
                    </TabsTrigger>
                    <TabsTrigger value="crypto" data-testid="tab-crypto" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-2">
                        Crypto USDT · TRC20
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="bre_b" className="mt-6">
                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-6 space-y-5">
                        <div className="rounded-xl border border-p2w-blue/40 bg-p2w-blue/10 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-p2w-blue font-bold">Número de llave Bre-B</div>
                            <div className="mt-2 flex items-center justify-between gap-3">
                                <code className="text-2xl font-sora font-bold text-white number-tabular" data-testid="brebkey-value">{cfg.brebkey}</code>
                                <Button onClick={() => copy(cfg.brebkey, "Número")} data-testid="copy-brebkey" variant="outline" className="border-p2w-line bg-transparent text-white gap-2">
                                    <Copy className="w-4 h-4" /> Copiar número
                                </Button>
                            </div>
                        </div>

                        <div className="flex items-start gap-2 rounded-xl border border-p2w-amber/40 bg-p2w-amber/10 px-4 py-3">
                            <AlertTriangle className="w-4 h-4 text-p2w-amber mt-0.5 shrink-0" />
                            <p className="text-sm text-p2w-amber">
                                Envía únicamente desde una cuenta bancaria personal a tu nombre. Pagos de terceros pueden ser rechazados.
                            </p>
                        </div>

                        <Field label="Monto transferido (COP)" required testid="brebamount">
                            <Input type="number" min={5000} step={1000} value={copAmount} onChange={(e) => setCopAmount(Number(e.target.value))} className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <Field label="Tu nombre (titular de la cuenta origen)" required testid="brebholder">
                            <Input value={holderName} onChange={(e) => setHolderName(e.target.value)} className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-p2w-gray">Comprobante de pago (obligatorio)</Label>
                            <label className="mt-2 flex items-center gap-3 rounded-xl border border-dashed border-p2w-line bg-p2w-elevated px-4 py-3 cursor-pointer hover:border-p2w-green/40">
                                <FileUp className="w-5 h-5 text-p2w-green" />
                                <span className="text-sm text-white/80">{proofFile ? proofFile.name : "Toca para adjuntar imagen o PDF"}</span>
                                <input type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="proof-file" />
                            </label>
                        </div>
                        <Button onClick={submitBreB} disabled={submitting} data-testid="submit-bre-b" className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold py-6 rounded-xl">
                            {submitting ? "Enviando…" : "Enviar solicitud de recarga"}
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="crypto" className="mt-6">
                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-6 space-y-5">
                        <div className="flex items-start gap-2 rounded-xl border border-p2w-red/40 bg-p2w-red/10 px-4 py-3">
                            <AlertTriangle className="w-4 h-4 text-p2w-red mt-0.5 shrink-0" />
                            <p className="text-sm text-p2w-red font-semibold">
                                Envía SOLO USDT por red TRC20. Otra red o moneda puede resultar en pérdida irreversible de fondos.
                            </p>
                        </div>
                        <div className="rounded-xl border border-p2w-blue/40 bg-p2w-blue/5 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-p2w-blue font-bold">Dirección USDT · TRC20</div>
                            <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                                <code className="text-sm font-mono text-white break-all" data-testid="usdt-address-value">{cfg.usdt_trc20_address}</code>
                                <Button onClick={() => copy(cfg.usdt_trc20_address, "Dirección")} data-testid="copy-usdt-address" variant="outline" className="border-p2w-line bg-transparent text-white gap-2 shrink-0">
                                    <Copy className="w-4 h-4" /> Copiar dirección
                                </Button>
                            </div>
                            <div className="mt-4 flex justify-center">
                                <img alt="QR USDT TRC20" className="rounded-lg bg-white p-2"
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(cfg.usdt_trc20_address || "")}`} />
                            </div>
                            <div className="mt-3 text-xs text-p2w-gray text-center">
                                1 USDT ≈ {Number(cfg.usdt_to_cop || 0).toLocaleString("es-CO")} COP (referencial)
                            </div>
                        </div>
                        <Field label="Monto enviado (USDT)" required testid="usdtamount">
                            <Input type="number" min={5} step={1} value={usdtAmount} onChange={(e) => setUsdtAmount(Number(e.target.value))} className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <Field label="Hash de la transacción (TX Hash)" required testid="usdttxhash">
                            <div className="relative">
                                <Hash className="w-4 h-4 text-p2w-gray absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="0x..." className="pl-9 bg-p2w-elevated border-p2w-line text-white font-mono text-xs" />
                            </div>
                        </Field>
                        <Button onClick={submitCrypto} disabled={submitting} data-testid="submit-crypto" className="w-full bg-p2w-blue text-white hover:bg-p2w-blue/90 font-semibold py-6 rounded-xl">
                            {submitting ? "Enviando…" : "Enviar solicitud de recarga"}
                        </Button>
                    </div>
                </TabsContent>
            </Tabs>

            {last && (
                <div className="mt-6 rounded-2xl border border-p2w-green/40 bg-p2w-green/10 p-5 flex items-center gap-3" data-testid="recarga-last">
                    <CheckCircle2 className="w-5 h-5 text-p2w-green" />
                    <div className="text-sm text-white">
                        Solicitud enviada · Ref <code className="text-p2w-green font-bold">{last.reference_code}</code>. Te notificaremos al aprobarla.
                    </div>
                </div>
            )}

            <p className="mt-6 text-xs text-p2w-gray">
                El bono debe usarse en pools; las ganancias se acreditan como saldo real.{" "}
                <Link to="/legal/terminos" className="text-p2w-green underline">Términos</Link>.
            </p>

            <h2 className="font-sora font-bold text-xl mt-12 mb-3">Mis recargas</h2>
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden">
                {history.length === 0 ? (
                    <div className="p-8 text-center text-p2w-gray text-sm">Aún no tienes recargas.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-p2w-elevated border-b border-p2w-line text-xs uppercase text-p2w-gray">
                            <tr><th className="text-left px-4 py-3">Ref</th><th className="text-left px-4 py-3">Método</th><th className="text-right px-4 py-3">Monto</th><th className="text-right px-4 py-3">Estado</th></tr>
                        </thead>
                        <tbody>
                            {history.map((d) => (
                                <tr key={d.id} className="border-t border-p2w-line">
                                    <td className="px-4 py-3 font-mono text-xs text-white">{d.reference_code}</td>
                                    <td className="px-4 py-3 text-white">{d.method === "bre_b" ? "Bre-B" : "Crypto"}</td>
                                    <td className="px-4 py-3 text-right number-tabular text-white">{d.amount.toLocaleString("es-CO")} {d.currency}</td>
                                    <td className="px-4 py-3 text-right"><StatusPill s={d.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Field({ label, children, required, testid }) {
    return (
        <div data-testid={testid ? `field-${testid}` : undefined}>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">
                {label} {required && <span className="text-p2w-amber">*</span>}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function StatusPill({ s }) {
    const map = {
        pending: ["bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", "Pendiente"],
        approving: ["bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", "Aprobando"],
        approved: ["bg-p2w-green/15 text-p2w-green border-p2w-green/40", "Aprobada"],
        rejected: ["bg-p2w-red/15 text-p2w-red border-p2w-red/40", "Rechazada"],
    };
    const [cls, label] = map[s] || map.pending;
    return <span className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider border font-bold ${cls}`}>{label}</span>;
}
